import React, { useState, useEffect } from 'react';
import { platformFeatures } from '../../data/mock';
import { ClipboardList, Users, Zap, Sparkles, Shield, ChevronLeft, ChevronRight } from 'lucide-react';

const iconMap = {
  ClipboardList,
  Users,
  Zap,
  Sparkles
};

const appScreenshots = [
  {
    id: 1,
    src: "https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/0stt2vil_A1C%20SUVPL%20APP%20LANDING%20PG%202025-12-27%20123651.png",
    alt: "A1C Survival Plan Home Dashboard"
  },
  {
    id: 2,
    src: "https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/fh6raw0w_A1C%20SUVPL%20APP-Journal-%202026-01-06%20161326.png",
    alt: "A1C Survival Plan Journal"
  },
  {
    id: 3,
    src: "https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/1aqmbxbo_A1C%20suvpl%20app-Diabetes%20Mgt%202026-01-06%20155009.png",
    alt: "A1C Survival Plan Diabetes Management"
  },
  {
    id: 4,
    src: "https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/mhcn03p1_A1C%20SUVPL%20APP-fitness%202026-01-06%20155606.png",
    alt: "A1C Survival Plan Fitness"
  },
  {
    id: 5,
    src: "https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/8pno2udx_A1C%20SUVPL%20APP-NUTRITION%202026-01-05%20131130.png",
    alt: "A1C Survival Plan Nutrition"
  },
  {
    id: 6,
    src: "https://customer-assets.emergentagent.com/job_3da1c7df-0ce6-4bcf-afe7-e3f44a8aed6b/artifacts/w47h81pg_A1C%20SUVPL%20APP-%20Mental%20Wellness-2026-01-02%20134830.png",
    alt: "A1C Survival Plan Mental Wellness"
  }
];

const tileBackgrounds = [
  "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80", // Daily Management - yoga/meditation
  "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80", // Health Teams - medical team
  "https://images.pexels.com/photos/1153369/pexels-photo-1153369.jpeg?auto=compress&w=800", // Active Living - healthy food
  "https://images.pexels.com/photos/289586/pexels-photo-289586.jpeg?auto=compress&w=800" // Peace of Mind - zen stones
];

const Platform = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % appScreenshots.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % appScreenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + appScreenshots.length) % appScreenshots.length);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white via-slate-50/50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-teal-100/30 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-100/30 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header with phone mockup */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16">
          {/* Left - Phone mockup with carousel */}
          <div className="relative flex justify-center lg:justify-start order-2 lg:order-1">
            {/* Phone mockup container */}
            <div className="relative">
              {/* Glow effect behind phone */}
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400/40 via-cyan-400/40 to-emerald-400/40 rounded-[3rem] blur-2xl transform scale-90" />
              
              {/* Phone frame */}
              <div className="relative bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-slate-900/40">
                <div className="bg-white rounded-[2.5rem] w-[280px] h-[580px] overflow-hidden relative">
                  {/* Carousel images */}
                  {appScreenshots.map((screenshot, index) => (
                    <img 
                      key={screenshot.id}
                      src={screenshot.src}
                      alt={screenshot.alt}
                      className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${
                        index === currentSlide ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  ))}
                </div>

                {/* Carousel navigation arrows */}
                <button 
                  onClick={prevSlide}
                  className="absolute left-[-20px] top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors z-10"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-600" />
                </button>
                <button 
                  onClick={nextSlide}
                  className="absolute right-[-20px] top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors z-10"
                >
                  <ChevronRight className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              {/* Carousel dots */}
              <div className="flex justify-center gap-2 mt-6">
                {appScreenshots.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-teal-500 w-6' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-3 shadow-lg border border-teal-100 animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">A1C Target</p>
                    <p className="text-sm font-bold text-emerald-600">On Track!</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-3 shadow-lg border border-cyan-100 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                    <Shield className="w-4 h-4 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Daily Goal</p>
                    <p className="text-sm font-bold text-cyan-600">85% Done</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Text content */}
          <div className="order-1 lg:order-2">
            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 text-sm font-medium rounded-full mb-6">
              Comprehensive Platform
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
              A1C Survival Plan is built
              <br />
              around one simple idea:
              <br />
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                your daily life tells a story
              </span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              A1C Survival Plan organizes and connects life's little moments into one clear picture.
            </p>
            
            <p className="text-lg text-slate-600 leading-relaxed mt-6">
              Doctor visits shouldn't feel like memory tests.
              <br />
              Your Journal becomes a living record of how you manage diabetes day-to-day — organized, connected, and ready when you need it.
            </p>
            
            <p className="text-xl font-semibold text-slate-800 mt-6">
              Track it. Understand it. Show it.
            </p>
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {platformFeatures.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div 
                key={feature.id}
                className="group relative rounded-3xl p-8 lg:p-10 border border-slate-100 hover:border-teal-200 shadow-sm hover:shadow-2xl hover:shadow-teal-100/50 transition-all duration-500 overflow-hidden min-h-[280px]"
              >
                {/* Background image */}
                <div className="absolute inset-0">
                  <img 
                    src={tileBackgrounds[index]}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay for readability - reduced opacity for bolder images */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-white/60 to-white/50 group-hover:from-white/60 group-hover:via-white/50 group-hover:to-white/40 transition-all duration-500" />
                </div>

                {/* Card number */}
                <div className="absolute top-8 right-8 text-6xl font-bold text-slate-200/50 group-hover:text-teal-200/50 transition-colors duration-300 z-10">
                  0{index + 1}
                </div>

                {/* Icon */}
                <div className="relative z-10 w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-teal-200 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                </div>

                {/* Content */}
                <h3 className="text-xl lg:text-2xl font-bold text-slate-800 mb-4 relative z-10">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed relative z-10">
                  {feature.description}
                </p>

                {/* Hover line accent */}
                <div className="absolute bottom-0 left-8 right-8 h-1 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Platform;
